import { useMemo, useRef } from 'react';
import {
  Flex,
  Text,
  Button,
  Icon,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import { useHighlightReplay } from '../shared/useHighlightReplay';
import { ConfigInput } from './ConfigInput';
import {
  type IFilterCondition,
  type IFilterableField,
  getOperatorsForDescriptor,
  operatorRequiresValue,
  isBooleanDescriptor,
  getDefaultValueForDescriptor,
} from './filter-utils';

/**
 * Props for the FilterConditionRow component
 */
export interface IFilterConditionRowProps {
  /** Available filterable fields (top-level and one-level-nested event fields) */
  fields: IFilterableField[];
  /** The condition this row represents */
  condition: IFilterCondition;
  /** Callback when the condition changes */
  onChange: (condition: IFilterCondition) => void;
  /** Callback to remove this condition */
  onRemove: () => void;
  /** Validation error message for this condition (e.g. empty required value) */
  error?: string;
  /** Whether the row is read-only */
  readOnly?: boolean;
  /**
   * When true, briefly pulses the row and shows a ✨ marker — used to flag a
   * filter the assistant just added, and scrolls it into view.
   */
  highlighted?: boolean;
  /**
   * Monotonic nonce that changes on every filter add. Depended on by the
   * highlight effect so re-marking the same condition replays the pulse/scroll
   * (the effect restarts the CSS animation via a reflow).
   */
  highlightNonce?: number;
}

/**
 * Groups fields by their `groupLabel` while preserving insertion order.
 * Top-level fields (`groupLabel: null`) are represented as groups with a null key.
 */
function groupFields(
  fields: IFilterableField[],
): { groupLabel: string | null; fields: IFilterableField[] }[] {
  const groups: { groupLabel: string | null; fields: IFilterableField[] }[] =
    [];
  const seen = new Map<string | null, IFilterableField[]>();

  for (const field of fields) {
    let bucket = seen.get(field.groupLabel ?? null);
    if (!bucket) {
      bucket = [];
      seen.set(field.groupLabel ?? null, bucket);
      groups.push({ groupLabel: field.groupLabel, fields: bucket });
    }
    bucket.push(field);
  }

  return groups;
}

/**
 * Renders the selected field label inside the SelectTrigger.
 *
 * Shows "group / label" for nested fields (e.g. "email / subject") and just
 * "label" for top-level fields — consistent with the MapInputsStep pattern.
 */
function FieldSelectValue({
  field,
  placeholder,
}: {
  field: IFilterableField | undefined;
  placeholder: string;
}) {
  if (!field) {
    return <span className="text-muted-foreground">{placeholder}</span>;
  }

  if (field.groupLabel) {
    return (
      <span className="truncate">
        <span className="text-muted-foreground">{field.groupLabel} /</span>
        {` ${field.label}`}
      </span>
    );
  }

  return <span className="truncate">{field.label}</span>;
}

/**
 * FilterConditionRow Component
 *
 * A single filter condition: [Field] [Operator] [Value] [Remove].
 *
 * The field dropdown groups nested fields (one level of dictionary nesting)
 * under a labelled `SelectGroup`. The value input is rendered by `ConfigInput`
 * from the selected field's concept descriptor, so it adapts to the field type
 * (text/number/boolean/enum). Value-less operators hide the value input.
 *
 * @param props - Component props
 * @returns JSX element containing one condition row
 */
export function FilterConditionRow({
  fields,
  condition,
  onChange,
  onRemove,
  error,
  readOnly = false,
  highlighted = false,
  highlightNonce,
}: IFilterConditionRowProps) {
  const { t } = useTranslation('automation-inputs');

  const rowRef = useRef<HTMLDivElement>(null);
  useHighlightReplay(rowRef, highlighted, highlightNonce);

  // Resolve the descriptor for the currently-selected field.
  const selectedField = useMemo(
    () => fields.find(f => f.name === condition.field),
    [fields, condition.field],
  );

  const operators = useMemo(
    () =>
      selectedField ? getOperatorsForDescriptor(selectedField.descriptor) : [],
    [selectedField],
  );

  // Boolean fields always use "equals"; we hide the operator dropdown entirely.
  const isBoolean = selectedField
    ? isBooleanDescriptor(selectedField.descriptor)
    : false;

  // Build a grouped structure for the field dropdown.
  const fieldGroups = useMemo(() => groupFields(fields), [fields]);

  /**
   * Handles field selection. The new field may have a different type, so reset
   * the operator to the first valid one and clear the value.
   */
  const handleFieldChange = (fieldName: string) => {
    const nextField = fields.find(f => f.name === fieldName);
    const nextOperators = nextField
      ? getOperatorsForDescriptor(nextField.descriptor)
      : [];
    onChange({
      ...condition,
      field: fieldName,
      operator: nextOperators[0] ?? 'equals',
      value: nextField
        ? getDefaultValueForDescriptor(nextField.descriptor)
        : undefined,
    });
  };

  const handleOperatorChange = (operator: string) => {
    onChange({
      ...condition,
      operator: operator as IFilterCondition['operator'],
      // Drop the value when switching to a value-less operator.
      value: operatorRequiresValue(operator as IFilterCondition['operator'])
        ? condition.value
        : undefined,
    });
  };

  const handleValueChange = (value: string | number | boolean) => {
    onChange({ ...condition, value });
  };

  const showValueInput =
    !!selectedField && operatorRequiresValue(condition.operator);

  const fieldPlaceholder = t('webhookDialog.filters.fieldPlaceholder');

  return (
    <Flex
      ref={rowRef}
      direction="row"
      gap={2}
      align="start"
      className={`w-full ${highlighted ? 'trigger-fill-highlight p-1' : ''}`}
    >
      {highlighted && (
        <Icon
          type="Sparkle"
          size="sm"
          aria-hidden
          className="text-primary trigger-fill-sparkle shrink-0 mt-2.5"
        />
      )}
      {/* Field */}
      <Select
        value={condition.field}
        onValueChange={handleFieldChange}
        disabled={readOnly}
      >
        <SelectTrigger className="w-full bg-background">
          <SelectValue placeholder={fieldPlaceholder} asChild>
            <FieldSelectValue
              field={selectedField}
              placeholder={fieldPlaceholder}
            />
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {fieldGroups.map(group =>
            group.groupLabel ? (
              // Nested fields: render under a labelled group.
              <SelectGroup key={group.groupLabel}>
                <SelectLabel>{group.groupLabel}</SelectLabel>
                {group.fields.map(field => (
                  <SelectItem key={field.name} value={field.name}>
                    {field.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            ) : (
              // Top-level fields: render directly without a group wrapper.
              group.fields.map(field => (
                <SelectItem key={field.name} value={field.name}>
                  {field.label}
                </SelectItem>
              ))
            ),
          )}
        </SelectContent>
      </Select>

      {/* Operator — hidden for boolean fields since it is always "equals" */}
      {!isBoolean && (
        <Select
          value={condition.operator}
          onValueChange={handleOperatorChange}
          disabled={readOnly || !selectedField}
        >
          <SelectTrigger className="w-full bg-background">
            <SelectValue
              placeholder={t('webhookDialog.filters.operatorPlaceholder')}
            />
          </SelectTrigger>
          <SelectContent>
            {operators.map(operator => (
              <SelectItem key={operator} value={operator}>
                {t(`webhookDialog.filters.operators.${operator}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Value — error renders inside the showValueInput guard.
          Safe because getInvalidFilterConditionIds guards on both condition.field
          and operatorRequiresValue before flagging a condition, so errors are only
          ever set when showValueInput would be true. If future validation adds error
          paths that don't depend on those guards, move the error rendering outside
          this block. */}
      <div className="w-full">
        {showValueInput && (
          <Flex direction="column" gap={1}>
            {/* Center the switch vertically to match the select trigger height */}
            <div className={isBoolean ? 'flex h-9 items-center' : undefined}>
              <ConfigInput
                descriptor={selectedField.descriptor}
                value={condition.value}
                onChange={handleValueChange}
                readOnly={readOnly}
              />
            </div>
            {error && (
              <Text className="text-xs text-destructive" role="alert">
                {error}
              </Text>
            )}
          </Flex>
        )}
      </div>

      {/* Remove */}
      {!readOnly && (
        <Button
          size="icon"
          variant="ghost"
          onClick={onRemove}
          aria-label={t('webhookDialog.filters.remove')}
          className="shrink-0"
        >
          <Icon type="Trash" size="sm" />
        </Button>
      )}
    </Flex>
  );
}
